import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { useTenantNavigate } from 'shared/hooks/useTenantNavigate';
import { tokens } from 'shared/theme';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';
import {
  renderDeploymentEnvironmentChip,
  renderDeploymentStatusChip,
  renderRecordStatusChip,
} from '../../shared/statusChips';
import useSoftwareService from '../../hooks/useSoftwareService';
import DeleteSoftwareDialog from './deleteSoftwareDialog';

const isDeleted = (row) => String(row?.status || row?._raw?.status || '').toUpperCase() === 'DELETED';

/** Format API LocalDate / ISO string as dd/mm/yyyy */
const formatDisplayDate = (value) => {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const dateColumn = (field, headerName, minWidth = 130) => ({
  field,
  headerName,
  flex: 0.75,
  minWidth,
  valueFormatter: (params) => formatDisplayDate(params.value),
});

const SoftwarePage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { navigateTenant } = useTenantNavigate();
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('system-register.software');
  const {
    items,
    totalRecords,
    isLoading,
    fetchItems,
    removeItem,
  } = useSoftwareService();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Live filter: debounce typing → update applied search term
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      setSearch((prev) => {
        if (prev === next) return prev;
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return next;
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(() => {
    fetchItems(paginationModel.page, paginationModel.pageSize, search, canViewAll && showAll);
  }, [fetchItems, paginationModel.page, paginationModel.pageSize, search, showAll, canViewAll]);

  useEffect(() => {
    if (!canViewAll && showAll) {
      setShowAll(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      items.map((s) => {
        const dates = s.deploymentStatusDates || {};
        return {
          id: s.id,
          softwareName: s.softwareName ?? '',
          internalReference: s.internalReference ?? '',
          externalOrganisationReference: s.externalOrganisationReference ?? '',
          contractStartDate: s.contractStartDate ?? '',
          contractEndDate: s.contractEndDate ?? '',
          deploymentStatus: s.deploymentStatus ?? '',
          licenseStartDate: s.licenseStartDate ?? '',
          licenseEndDate: s.licenseEndDate ?? '',
          deploymentEnvironment: s.deploymentEnvironment ?? '',
          systemIpAddress: s.systemIpAddress ?? '',
          systemUrl: s.systemUrl ?? '',
          hostingServerName: s.hostingServerName ?? '',
          plannedDate: dates.planned ?? '',
          developmentDate: dates.development ?? '',
          testingDate: dates.testing ?? '',
          pilotDate: dates.pilot ?? '',
          productionDate: dates.production ?? s.firstDeployedDate ?? '',
          retiredDate: dates.retired ?? '',
          suspendedDate: dates.suspended ?? '',
          status: s.status || 'ACTIVE',
          _raw: s,
        };
      }),
    [items]
  );

  const columns = useMemo(
    () => [
      {
        field: 'softwareName',
        headerName: 'Software name',
        flex: 1.1,
        minWidth: 160,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'internalReference',
        headerName: 'Internal ID',
        flex: 0.7,
        minWidth: 110,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'externalOrganisationReference',
        headerName: 'Organisation reference ID',
        flex: 0.9,
        minWidth: 160,
        valueFormatter: (params) => params.value || '—',
      },
      dateColumn('contractStartDate', 'Contract start date'),
      dateColumn('contractEndDate', 'Contract end date'),
      {
        field: 'deploymentStatus',
        headerName: 'Deployment status',
        flex: 0.85,
        minWidth: 150,
        renderCell: renderDeploymentStatusChip,
      },
      dateColumn('licenseStartDate', 'License start date'),
      dateColumn('licenseEndDate', 'License end date'),
      {
        field: 'deploymentEnvironment',
        headerName: 'Deployment environment',
        flex: 0.85,
        minWidth: 160,
        renderCell: renderDeploymentEnvironmentChip,
      },
      {
        field: 'systemIpAddress',
        headerName: 'System IP address',
        flex: 0.85,
        minWidth: 140,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'systemUrl',
        headerName: 'System URL / path',
        flex: 1.1,
        minWidth: 180,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'hostingServerName',
        headerName: 'Server name hosting the application',
        flex: 1.1,
        minWidth: 200,
        valueFormatter: (params) => params.value || '—',
      },
      dateColumn('plannedDate', 'Planned', 120),
      dateColumn('developmentDate', 'Development', 120),
      dateColumn('testingDate', 'Testing', 120),
      dateColumn('pilotDate', 'Pilot', 120),
      dateColumn('productionDate', 'Production', 120),
      dateColumn('retiredDate', 'Retired', 120),
      dateColumn('suspendedDate', 'Suspended', 120),
      ...(showAll && canViewAll
        ? [
            {
              field: 'status',
              headerName: 'Record status',
              width: 130,
              renderCell: ({ row }) =>
                renderRecordStatusChip({
                  value: isDeleted(row) ? 'DELETED' : row.status || 'ACTIVE',
                  row,
                }),
            },
          ]
        : []),
      {
        field: 'actions',
        headerName: 'Actions',
        width: 150,
        sortable: false,
        filterable: false,
        hideable: false,
        renderCell: ({ row }) => {
          const deleted = isDeleted(row);
          return (
            <Box display="flex" alignItems="center" gap={0.25}>
              <Tooltip title="View report">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() =>
                      navigateTenant(`/system-register/software/${row.id}/report`)
                    }
                    disabled={deleted}
                    aria-label="View report"
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Edit system">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigateTenant(`/system-register/software/${row.id}/details`)}
                    disabled={deleted}
                    aria-label="Edit"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={deleted ? 'Already deleted' : 'Mark as deleted'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget(row._raw)}
                    disabled={deleted}
                    aria-label="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [navigateTenant, showAll, canViewAll]
  );

  return (
    <Box m="20px">
      <DeleteSoftwareDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onSuccess={load}
        row={deleteTarget}
        removeItem={removeItem}
      />

      <Header
        title="Add and View all registered Systems"
        subtitle="Register new systems or browse and manage existing ones"
      />

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mt={2}
        mb={1.5}
        gap={2}
        flexWrap="wrap"
      >
        <Box display="flex" gap={1.25} alignItems="center" flexWrap="wrap" flex={1} justifyContent="flex-end">
          <TextField
            size="small"
            placeholder="Search systems by name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 280 },
              maxWidth: 420,
              flex: { xs: '1 1 100%', sm: '0 1 360px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#fff',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchInput('')}
                    edge="end"
                  >
                    <ClearOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {canViewAll ? (
            <Button
              variant={showAll ? 'contained' : 'outlined'}
              color="secondary"
              onClick={() => {
                setPaginationModel((p) => ({ ...p, page: 0 }));
                setShowAll((v) => !v);
              }}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {showAll ? 'Active only' : 'View all'}
            </Button>
          ) : null}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigateTenant('/system-register/software/register')}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Add System
          </Button>
        </Box>
      </Box>

      <Box position="relative" sx={{ opacity: isLoading ? 0.4 : 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          pagination
          paginationMode="server"
          rowCount={totalRecords}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: buildGridToolbarExportProps({
              title: 'Registered Systems',
              tableId: 'system-register.software',
              showQuickFilter: false,
            }),
          }}
          getRowClassName={(params) => (isDeleted(params.row) ? 'row-deleted' : '')}
          sx={{
            '& .row-deleted': { opacity: 0.65 },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
          }}
        />
        {isLoading ? (
          <Box position="absolute" top="40%" left="50%" sx={{ transform: 'translate(-50%, -50%)' }}>
            <LoadingSpinner message="Loading systems…" />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default SoftwarePage;
